import { formatDateRange } from '@/utils';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useGetOrganizationDisciplineStatesQuery } from '../../../../../../app/features/employee/management-dashboard/management-dashboard.api';
import CustomRangePicker from '../../../../../../components/CustomRangePicker';
import useError from '../../../../../../hooks/useError';
import type { ManagementFilters } from '../../../../../../hooks/useManagementFilters';

const CustomTooltip = (props: any) => {
  const { active, payload, label } = props;

  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0F172A] text-white p-2 rounded border border-[#1f2d40] text-xs">
        <p className="text-center font-bold mb-1">
          {dayjs(label).format('MMM YYYY')}
        </p>
        <p className="text-[#B6AD00]">{payload[0].value?.toFixed(2)}%</p>
      </div>
    );
  }

  return null;
};

const DisciplineTrend = ({
  department_id,
  office_id,
  filter,
  setFilter,
  isFilterChanged,
  resetFilter,
}: {
  department_id?: string;
  office_id?: string;
  filter: ManagementFilters;
  setFilter: (key: any, value: any) => void;
  isFilterChanged: (key: any) => boolean;
  resetFilter: (key: any) => void;
}) => {
  const disciplineFilter = filter.disciplineTrend;

  const { data: trendData, error } = useGetOrganizationDisciplineStatesQuery({
    start_date: disciplineFilter.start_date || '',
    end_date: disciplineFilter.end_date || '',
    department_id,
    office_id,
  });
  useError(error, 'Failed to fetch discipline trend stats');

  const chartData = (trendData?.data?.data?.monthly_scores || []).map(
    (item) => ({
      name: item.month,
      value: item.average_wds,
    }),
  );

  return (
    <div className="bg-theme-slate-900 border border-theme-gray-700 p-4 rounded-xl w-full">
      <Flex
        justify="space-between"
        align="center"
        className="mb-4 text-white p-2"
      >
        <h4 className="text-lg px-3 py-1 rounded border border-theme-gray-700 text-white leading-tight">
          Discipline Trend
          <span className="text-theme-primary block text-sm">
            {formatDateRange(
              disciplineFilter.start_date,
              disciplineFilter.end_date,
            )}
          </span>
        </h4>

        <CustomRangePicker
          picker="month"
          startDate={disciplineFilter.start_date}
          endDate={disciplineFilter.end_date}
          onChange={(start, end) => {
            const s = dayjs(start).startOf('month').format('YYYY-MM-DD');
            const e = dayjs(end).endOf('month').format('YYYY-MM-DD');
            setFilter('disciplineTrend', { start_date: s, end_date: e });
          }}
          disabledDate={(current) =>
            current > dayjs().subtract(1, 'month').endOf('month')
          }
          isChanged={isFilterChanged('disciplineTrend')}
          onReset={() => resetFilter('disciplineTrend')}
        />
      </Flex>

      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="colorTrend" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#B6AD00" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#B6AD00" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#1e2a38"
            vertical={false}
          />
          <XAxis
            dataKey="name"
            stroke="#94a3b8"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10 }}
            tickFormatter={(val) => dayjs(val).format('MMM')}
          />
          <YAxis
            stroke="#94a3b8"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10 }}
            tickFormatter={(value) => `${value}%`}
            domain={[0, 100]}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: '#ffffff', strokeWidth: 1 }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke="#D3E700"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorTrend)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DisciplineTrend;
